/**
 * asset-location — derive a human, physical "where does this data live and how
 * do I reach it" descriptor from the fields we already have on an asset
 * (sourceType + displayName + optional countryCode). No backend change needed:
 * every DSPM asset in the graph / DSAR walk already carries these.
 *
 * Used by the Lineage inspector and the subject-data-search result so an operator sees
 * "AWS S3 · ap-south-1 (Mumbai) · bucket apexpay-lake · key incoming/..." rather
 * than just a slug. Defensive by construction: unknown shapes degrade to a
 * generic self-managed descriptor and never throw.
 */

export interface AssetLocation {
  /** Human provider name, e.g. "AWS", "Microsoft Azure", "Self-managed". */
  provider: string;
  /** Short key for icon/accent lookups. */
  providerKey: 'aws' | 'azure' | 'gcp' | 'snowflake' | 'mongodb' | 'm365' | 'databricks' | 'self';
  /** Service, e.g. "S3 bucket", "PostgreSQL", "BigQuery dataset". */
  service: string;
  /** Cloud region code, e.g. "ap-south-1". Null for pure on-prem where unknown. */
  region: string | null;
  /** Friendly region city, e.g. "Mumbai". */
  regionLabel: string | null;
  /** Top container: bucket / database / dataset / cluster / site. */
  container: string | null;
  /** What `container` is: "Bucket", "Database", "Dataset", ... */
  containerKind: string | null;
  /** Path within the container: object key / schema.table / collection. */
  path: string | null;
  /** What `path` is: "Object key", "Schema.table", "Collection", ... */
  pathKind: string | null;
  /** One-line "how to reach it" hint an engineer can act on. */
  reach: string;
  /** Accent colour (provider brand-ish) for chips. */
  accent: string;
}

const CLOUD_OBJECT = new Set(['s3', 'azure_blob', 'gcs']);

function countryFrom(displayName: string, countryCode?: string | null): 'IN' | 'US' | 'EU' {
  if (countryCode) {
    const c = countryCode.toUpperCase();
    if (c === 'US') return 'US';
    if (c === 'IN') return 'IN';
    if (['DE', 'FR', 'NL', 'IE', 'EU'].includes(c)) return 'EU';
  }
  // Infer from name tokens when countryCode is absent (DSAR response omits it).
  const n = displayName.toLowerCase();
  if (/\b(us|useast|us-east|virginia|useast1)\b/.test(n) || n.includes('.us.') || n.includes('/us/')) return 'US';
  if (/\b(eu|europe|frankfurt|ireland)\b/.test(n)) return 'EU';
  return 'IN';
}

interface RegionPair { region: string; label: string; }

function regionFor(providerKey: AssetLocation['providerKey'], country: 'IN' | 'US' | 'EU'): RegionPair | null {
  const map: Record<string, Record<string, RegionPair>> = {
    aws: {
      IN: { region: 'ap-south-1', label: 'Mumbai' },
      US: { region: 'us-east-1', label: 'N. Virginia' },
      EU: { region: 'eu-central-1', label: 'Frankfurt' },
    },
    azure: {
      IN: { region: 'centralindia', label: 'Pune' },
      US: { region: 'eastus', label: 'Virginia' },
      EU: { region: 'westeurope', label: 'Amsterdam' },
    },
    gcp: {
      IN: { region: 'asia-south1', label: 'Mumbai' },
      US: { region: 'us-central1', label: 'Iowa' },
      EU: { region: 'europe-west3', label: 'Frankfurt' },
    },
    snowflake: {
      IN: { region: 'ap-south-1', label: 'Mumbai (AWS)' },
      US: { region: 'us-east-1', label: 'N. Virginia (AWS)' },
      EU: { region: 'eu-central-1', label: 'Frankfurt (AWS)' },
    },
    databricks: {
      IN: { region: 'ap-south-1', label: 'Mumbai' },
      US: { region: 'us-east-1', label: 'N. Virginia' },
      EU: { region: 'eu-central-1', label: 'Frankfurt' },
    },
    mongodb: {
      IN: { region: 'ap-south-1', label: 'Mumbai' },
      US: { region: 'us-east-1', label: 'N. Virginia' },
      EU: { region: 'eu-central-1', label: 'Frankfurt' },
    },
    self: {
      IN: { region: 'ap-south-1', label: 'Mumbai (VPC)' },
      US: { region: 'us-east-1', label: 'N. Virginia (VPC)' },
      EU: { region: 'eu-central-1', label: 'Frankfurt (VPC)' },
    },
  };
  return map[providerKey]?.[country] ?? null;
}

const ACCENT: Record<AssetLocation['providerKey'], string> = {
  aws: '#ff9900',
  azure: '#0089d6',
  gcp: '#4285f4',
  snowflake: '#29b5e8',
  mongodb: '#10aa50',
  m365: '#d83b01',
  databricks: '#ff3621',
  self: '#8b93a7',
};

/** Split a dotted DB slug into { container, path } with sensible fallbacks. */
function splitDotted(rest: string): { container: string | null; path: string | null } {
  const parts = rest.split('.').filter(Boolean);
  if (parts.length >= 3) return { container: parts[0], path: parts.slice(1).join('.') };
  if (parts.length === 2) return { container: null, path: parts.join('.') };
  return { container: null, path: rest || null };
}

export function deriveLocation(
  sourceType: string | undefined,
  displayName: string,
  countryCode?: string | null,
): AssetLocation {
  const src = (sourceType ?? '').toLowerCase();
  const name = displayName ?? '';
  const country = countryFrom(name, countryCode);

  const base = (
    providerKey: AssetLocation['providerKey'],
    provider: string,
    service: string,
    extra: Partial<AssetLocation>,
  ): AssetLocation => {
    const rp = regionFor(providerKey, country);
    return {
      provider,
      providerKey,
      service,
      region: rp?.region ?? null,
      regionLabel: rp?.label ?? null,
      container: extra.container ?? null,
      containerKind: extra.containerKind ?? null,
      path: extra.path ?? null,
      pathKind: extra.pathKind ?? null,
      reach: extra.reach ?? '',
      accent: ACCENT[providerKey],
    };
  };

  // ── cloud object storage (s3 / azure blob / gcs) ────────────────────────
  if (CLOUD_OBJECT.has(src)) {
    const stripped = name.replace(/^s3:\/\//, '').replace(/^azureblob:\/\//, '').replace(/^gcs:\/\//, '');
    const slash = stripped.indexOf('/');
    const container = slash >= 0 ? stripped.slice(0, slash) : stripped;
    const path = slash >= 0 ? stripped.slice(slash + 1) : null;
    if (src === 's3') {
      const rp = regionFor('aws', country);
      return base('aws', 'AWS', 'S3 bucket', {
        container, containerKind: 'Bucket', path, pathKind: 'Object key',
        reach: `AWS Console → S3 → ${container}${path ? ` → ${path}` : ''} (${rp?.region ?? ''})`,
      });
    }
    if (src === 'azure_blob') {
      return base('azure', 'Microsoft Azure', 'Blob container', {
        container, containerKind: 'Container', path, pathKind: 'Blob path',
        reach: `Azure Portal → Storage → Containers → ${container}${path ? ` → ${path}` : ''}`,
      });
    }
    return base('gcp', 'Google Cloud', 'Cloud Storage bucket', {
      container, containerKind: 'Bucket', path, pathKind: 'Object path',
      reach: `GCP Console → Cloud Storage → ${container}${path ? ` → ${path}` : ''}`,
    });
  }

  // ── warehouses / lakehouses ─────────────────────────────────────────────
  if (src === 'snowflake') {
    const { container, path } = splitDotted(name.replace(/^snowflake\./, ''));
    return base('snowflake', 'Snowflake', 'Snowflake table', {
      container, containerKind: 'Database', path, pathKind: 'Schema.table',
      reach: `Snowsight → Databases → ${path ?? name}`,
    });
  }
  if (src === 'bigquery') {
    const { container, path } = splitDotted(name.replace(/^bq\./, ''));
    return base('gcp', 'Google Cloud', 'BigQuery table', {
      container, containerKind: 'Dataset', path, pathKind: 'Dataset.table',
      reach: `GCP Console → BigQuery → ${path ?? name}`,
    });
  }
  if (src === 'databricks') {
    const { container, path } = splitDotted(name.replace(/^databricks\./, ''));
    return base('databricks', 'Databricks', 'Delta table', {
      container, containerKind: 'Schema', path, pathKind: 'Schema.table',
      reach: `Databricks → Catalog → ${path ?? name}`,
    });
  }
  if (src === 'redshift') {
    const { container, path } = splitDotted(name.replace(/^redshift\./, ''));
    return base('aws', 'AWS', 'Redshift table', {
      container, containerKind: 'Schema', path, pathKind: 'Schema.table',
      reach: `AWS Console → Redshift → Query editor → ${path ?? name}`,
    });
  }

  // ── document / nosql ────────────────────────────────────────────────────
  if (src === 'mongodb') {
    const { container, path } = splitDotted(name.replace(/^mongo\./, ''));
    return base('mongodb', 'MongoDB Atlas', 'MongoDB collection', {
      container, containerKind: 'Cluster', path, pathKind: 'Collection',
      reach: `Atlas → Clusters → ${container ?? 'cluster'} → Collections → ${path ?? name}`,
    });
  }

  // ── M365 / Exchange Online ──────────────────────────────────────────────
  // name is `<mailbox>/<message id>[/<attachment id>/<file name>]`. Checked
  // before the SharePoint branch, which takes every other m365 type.
  if (src === 'm365_exchange') {
    const segs = name.replace(/^m365mail:\/\//, '').split('/').filter(Boolean);
    const mailbox = segs.length > 1 ? segs[0] : null;
    const path = segs.length > 1 ? segs.slice(1).join('/') : (segs[0] ?? null);
    const rp = country === 'US' ? 'United States' : country === 'EU' ? 'Europe' : 'India';
    return {
      provider: 'Microsoft 365', providerKey: 'm365', service: 'Exchange Online mailbox',
      region: null, regionLabel: rp, container: mailbox, containerKind: 'Mailbox',
      path, pathKind: 'Message',
      reach: `Microsoft 365 → Exchange Online → ${mailbox ?? 'mailbox'} → message ${path ?? name}`,
      accent: ACCENT.m365,
    };
  }

  // ── M365 / SharePoint ───────────────────────────────────────────────────
  if (src === 'm365_sharepoint' || src.startsWith('m365')) {
    const site = name.replace(/^m365sp:\/\//, '').replace(/^m365[a-z]*:\/\//, '');
    const rp = country === 'US' ? 'United States' : country === 'EU' ? 'Europe' : 'India';
    return {
      provider: 'Microsoft 365', providerKey: 'm365', service: 'SharePoint site',
      region: null, regionLabel: rp, container: site || null, containerKind: 'Site',
      path: null, pathKind: null,
      reach: `Microsoft 365 → SharePoint → ${site}`, accent: ACCENT.m365,
    };
  }

  // ── employee laptop / endpoint (FOCTTA Endpoint Sensor) ─────────────────
  // On-device data-at-rest discovery. name is like
  // "LAPTOP-RETAIL-042\Downloads\file.csv" - split the device off the path.
  if (src === 'endpoint' || src === 'laptop' || src === 'endpoint_fs') {
    const segs = name.replace(/\\/g, '/').split('/').filter(Boolean);
    const device = segs.length > 1 ? segs[0] : null;
    const path = segs.length > 1 ? segs.slice(1).join('/') : (segs[0] ?? null);
    return {
      provider: 'Employee laptop', providerKey: 'self',
      service: 'FOCTTA Endpoint Sensor (on-device)',
      region: null, regionLabel: 'On-device · in-country',
      container: device, containerKind: 'Device',
      path, pathKind: 'File on disk',
      reach: `On device ${device ?? 'endpoint'} → ${path ?? name}. Scanned in place; only metadata left the laptop.`,
      accent: ACCENT.self,
    };
  }

  // ── file servers ────────────────────────────────────────────────────────
  if (src === 'smb_server' || src === 'local_filesystem' || src === 'smb') {
    return {
      provider: 'File server (SMB/CIFS)', providerKey: 'self', service: 'File share',
      region: regionFor('self', country)?.region ?? null,
      regionLabel: regionFor('self', country)?.label ?? null,
      container: null, containerKind: 'Share', path: name || null, pathKind: 'UNC path',
      reach: `Open ${name} on the file server (your Windows identity)`, accent: ACCENT.self,
    };
  }

  // ── IBM i (Db2 for i) ───────────────────────────────────────────────────
  // Refs are db2i://<system>/<LIBRARY>.<FILE>[.<FIELD>]. The container is a
  // LIBRARY, not a database, and an IBM i administrator looks for it by that
  // name; the generic branch below would print "DB2I" and "Database".
  if (src === 'db2i') {
    const { container, path } = splitDotted(name.replace(/^db2i:\/\/[^/]*\//, ''));
    const rp = regionFor('self', country);
    return {
      provider: 'Self-managed (on-premises)', providerKey: 'self', service: 'IBM i (Db2 for i)',
      region: rp?.region ?? null, regionLabel: rp?.label ?? null,
      container, containerKind: container ? 'IBM i library' : null,
      path, pathKind: 'Library.file',
      reach: `Sign on to the IBM i and open ${container ? `${container}/` : ''}${path ?? name} (IBM i Access Client Solutions, or WRKOBJ)`,
      accent: ACCENT.self,
    };
  }

  // ── self-managed / cloud databases (postgres/mysql/mssql/oracle/…) ───────
  const engineName: Record<string, string> = {
    postgres: 'PostgreSQL', mysql: 'MySQL', mssql: 'SQL Server', oracle: 'Oracle DB',
    cassandra: 'Cassandra', elasticsearch: 'Elasticsearch', redis: 'Redis',
  };
  const engine = engineName[src] ?? (src ? src.toUpperCase() : 'Data store');
  const { container, path } = splitDotted(name);
  const rp = regionFor('self', country);
  return {
    provider: 'Self-managed (in-VPC)', providerKey: 'self', service: engine,
    region: rp?.region ?? null, regionLabel: rp?.label ?? null,
    container, containerKind: container ? 'Database' : null,
    path, pathKind: 'Schema.table',
    reach: `Connect to the ${engine} instance in your VPC → ${path ?? name}`,
    accent: ACCENT.self,
  };
}

/** Short "AWS · ap-south-1 · S3" style summary chip text. */
export function locationSummary(loc: AssetLocation): string {
  const bits = [loc.provider, loc.region ? `${loc.region}${loc.regionLabel ? ` (${loc.regionLabel})` : ''}` : loc.regionLabel]
    .filter(Boolean);
  return bits.join(' · ');
}
